package com.swetdot.swetgram.domain

/**
 * The one-line summary under a chat's name in the list.
 *
 * The shape follows the push-preview rules in DECISIONS §9, deliberately: a notification and
 * the chat list describe the same message, and if they disagree the app looks broken.
 *
 *   - direct chat:  the message text alone
 *   - group chat:   "Sender: message"
 *   - image:        "Sent a photo"
 *   - own message:  prefixed with "You: " so a user can tell whose turn it is
 *
 * Returns a structured result rather than a finished string so the UI can style the prefix
 * differently from the body, and so the strings can be localized (EN + RU per DECISIONS §1)
 * without this logic needing to know about locales.
 */
data class ChatPreview(
    /** "Alex", "You", or null in a direct chat where the peer's name is already the title. */
    val speaker: String?,
    val body: PreviewBody,
)

sealed interface PreviewBody {
    data class Text(val value: String) : PreviewBody

    /** Rendered as "Sent a photo" / "Отправлено фото" -- the caller supplies the string. */
    data object Photo : PreviewBody

    data object Voice : PreviewBody

    data object Deleted : PreviewBody

    /** No messages yet. */
    data object Empty : PreviewBody
}

fun Chat.preview(me: UserId): ChatPreview {
    val message = lastMessage ?: return ChatPreview(speaker = null, body = PreviewBody.Empty)

    val speaker = when {
        message.isMine(me) -> SPEAKER_SELF
        // In a direct chat the title is already the other person's name, so repeating it in
        // the preview wastes the line.
        kind == ChatKind.DIRECT -> null
        else -> message.sender.displayName
    }

    val body = when {
        message.isDeleted -> PreviewBody.Deleted
        message.kind == MessageKind.IMAGE -> PreviewBody.Photo
        message.kind == MessageKind.VOICE -> PreviewBody.Voice
        message.picso != null -> PreviewBody.Photo
        // A text message with no text is not worth a blank line; treat it as empty.
        message.text.isNullOrBlank() -> PreviewBody.Empty
        else -> PreviewBody.Text(message.text.collapseWhitespace())
    }

    return ChatPreview(speaker, body)
}

/** Sentinel the UI swaps for a localized "You". */
const val SPEAKER_SELF = " self"

/**
 * A pasted multi-line message would otherwise blow the row height apart, and trailing spaces
 * would make the ellipsis appear in the wrong place.
 */
private fun String.collapseWhitespace(): String =
    trim().replace(Regex("\\s+"), " ")
